import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CurrencyDollarIcon, 
  ChartBarIcon, 
  LockClosedIcon,
  RocketLaunchIcon,
  LinkIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/SimpleAuthContext';
import toast from 'react-hot-toast';

const SimpleDashboard = () => {
  const { user } = useAuth();
  const [tokenLaunched, setTokenLaunched] = useState(false);
  const [linkedToken, setLinkedToken] = useState(null);
  const [showLinkTokenModal, setShowLinkTokenModal] = useState(false);
  const [customTokenName, setCustomTokenName] = useState('');

  // Generate token name from handle (e.g., @john becomes $JOHN)
  const cleanHandle = user?.handle ? user.handle.replace('@', '').replace('demo_', '') : '';
  const defaultTokenName = cleanHandle ? `$${cleanHandle.toUpperCase()}` : '';
  const displayTokenName = linkedToken || defaultTokenName || '$TOKEN';

  const handleLaunchToken = () => {
    toast.success(`🚀 Launching ${defaultTokenName} token on HandCash!`);
    setTokenLaunched(true);
    // Here you would integrate with HandCash token launch API
  };

  const handleLinkDifferentToken = () => {
    if (!customTokenName) {
      toast.error('Please enter a token name');
      return;
    }
    setLinkedToken(customTokenName.toUpperCase());
    setShowLinkTokenModal(false);
    toast.success(`✅ Linked to ${customTokenName.toUpperCase()} token`);
  };

  const stats = [
    {
      name: 'Your Token',
      value: displayTokenName,
      icon: ChartBarIcon,
      color: tokenLaunched || linkedToken ? 'bg-green-500' : 'bg-gray-400',
      description: tokenLaunched || linkedToken ? 'Active' : 'Not launched yet',
      isToken: true
    },
    {
      name: 'Token Holders',
      value: tokenLaunched || linkedToken ? '0' : '-',
      icon: SparklesIcon,
      color: 'bg-blue-500',
      description: 'Total holders'
    },
    {
      name: 'Total Distributed',
      value: '0.0000',
      unit: 'BSV',
      icon: CurrencyDollarIcon,
      color: 'bg-yellow-500',
      description: 'Lifetime distributions'
    },
    {
      name: 'Next Distribution',
      value: tokenLaunched || linkedToken ? 'Not scheduled' : '-',
      icon: RocketLaunchIcon,
      color: 'bg-purple-500',
      description: 'Upcoming payout'
    }
  ];

  return (
    <div className="min-h-screen bg-black py-8">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-black mb-2">
            Welcome, {user?.handle || user?.displayName}! 🚀
          </h1>
          <p className="text-gray-300">
            Create & manage your token dividend distributions on HandCash
          </p>
        </div>

        {/* Token Launch Section */}
        {!tokenLaunched && !linkedToken && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-black border-minimal rounded-xl p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-black mb-2">
                  Launch Your Token on HandCash
                </h3>
                <p className="text-gray-400 mb-4">
                  Your default token will be <span className="text-white font-mono">{defaultTokenName}</span> based on your handle
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={handleLaunchToken}
                  className="bg-white text-black px-6 py-3 rounded-lg font-semibold hover-minimal border-minimal"
                >
                  <RocketLaunchIcon className="h-5 w-5 inline mr-2" />
                  Launch {defaultTokenName}
                </button>
                <button
                  onClick={() => setShowLinkTokenModal(true)}
                  className="bg-gray-700 text-black px-6 py-3 rounded-lg font-semibold hover:bg-gray-600 transition"
                >
                  <LinkIcon className="h-5 w-5 inline mr-2" />
                  Link Different Token
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Token Status Alert */}
        {(tokenLaunched || linkedToken) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-green-900/30 border border-green-500/50 rounded-xl p-4"
          >
            <div className="flex items-center">
              <LockClosedIcon className="h-5 w-5 text-green-400 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-green-400">
                  Token Active: {displayTokenName}
                </h3>
                <p className="text-sm text-green-300/70">
                  {linkedToken ? 'Linked to existing token' : 'Successfully launched on HandCash'}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border ${
                stat.isToken && !tokenLaunched && !linkedToken
                  ? 'border-gray-600/50 opacity-60'
                  : 'border-minimal hover-minimal'
              } transition-all`}
            >
              <div className="flex items-center">
                <div className={`${stat.color} rounded-lg p-3 mr-4`}>
                  <stat.icon className="h-6 w-6 text-black" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-400">{stat.name}</p>
                  <p className={`text-2xl font-bold ${
                    stat.isToken && !tokenLaunched && !linkedToken ? 'text-gray-500' : 'text-black'
                  }`}>
                    {stat.value} {stat.unit || ''}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Main Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Configure Distributions */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border ${
              tokenLaunched || linkedToken ? 'border-minimal' : 'border-gray-600/50 opacity-60'
            }`}
          >
            <h2 className="text-xl font-bold text-black mb-4">
              Configure Distributions
            </h2>
            
            {tokenLaunched || linkedToken ? (
              <div>
                <p className="text-gray-400 mb-4">
                  Set up automatic dividend distributions for {displayTokenName} holders
                </p>
                <button className="w-full bg-white text-black py-3 rounded-lg font-semibold hover-minimal border-minimal">
                  Configure Distribution Rules
                </button>
              </div>
            ) : (
              <div className="text-center py-8">
                <CurrencyDollarIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500">Launch or link a token first</p>
              </div>
            )}
          </motion.div>

          {/* Token Holder Management */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border ${
              tokenLaunched || linkedToken ? 'border-minimal' : 'border-gray-600/50 opacity-60'
            }`}
          >
            <h2 className="text-xl font-bold text-black mb-4">
              Token Holders
            </h2>
            
            {tokenLaunched || linkedToken ? (
              <div>
                <p className="text-gray-400 mb-4">
                  View and manage {displayTokenName} token holders
                </p>
                <button className="w-full bg-gray-700 text-black py-3 rounded-lg font-semibold hover:bg-gray-600 transition">
                  View Holder List
                </button>
              </div>
            ) : (
              <div className="text-center py-8">
                <ChartBarIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500">No token holders yet</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Link Token Modal */}
        {showLinkTokenModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4"
            >
              <h3 className="text-xl font-bold text-black mb-4">Link Existing Token</h3>
              <p className="text-gray-400 mb-4">
                Enter the token symbol you want to link for dividend distributions
              </p>
              <input
                type="text"
                placeholder="e.g., NPG, PEPE, etc."
                value={customTokenName}
                onChange={(e) => setCustomTokenName(e.target.value)}
                className="w-full bg-gray-700 text-black px-4 py-2 rounded-lg mb-4 focus:outline-none focus:outline-none"
              />
              <div className="flex gap-4">
                <button
                  onClick={handleLinkDifferentToken}
                  className="flex-1 bg-white text-black py-2 rounded-lg font-semibold"
                >
                  Link Token
                </button>
                <button
                  onClick={() => setShowLinkTokenModal(false)}
                  className="flex-1 bg-gray-700 text-black py-2 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleDashboard;